import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Award, CheckCircle, ArrowRight, BadgeCheck, Search, FileCheck, Star, Eye } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const GRADING_COMPANIES = [
  { name: 'PSA', full: 'Professional Sports Authenticator', description: 'The industry standard for sports cards and trading cards. PSA grades on a 1-10 scale with half-point increments for gem mint.', specialties: ['Sports Cards', 'Pokemon', 'Trading Cards'] },
  { name: 'BGS / Beckett', full: 'Beckett Grading Services', description: 'Known for sub-grade breakdowns (centering, corners, edges, surface). BGS 9.5 Gem Mint and BGS 10 Pristine are highly sought after.', specialties: ['Sports Cards', 'TCG', 'Gaming Cards'] },
  { name: 'CGC', full: 'Certified Guaranty Company', description: 'Originally known for comic books, CGC now grades trading cards. Offers a unique holder design and is growing rapidly in the hobby.', specialties: ['Comic Books', 'Trading Cards', 'Magazines'] },
  { name: 'SGC', full: 'Sportscard Guaranty Corporation', description: 'Popular for vintage cards. Known for fast turnaround times and the tuxedo black label for perfect 10 grades.', specialties: ['Vintage Sports Cards', 'Pre-war Cards'] },
  { name: 'NGC', full: 'Numismatic Guaranty Company', description: 'The gold standard for coin grading. NGC uses the Sheldon 1-70 scale and is trusted worldwide for coins and medals.', specialties: ['Coins', 'Medals', 'Tokens'] },
  { name: 'PCGS', full: 'Professional Coin Grading Service', description: 'Leading coin authentication and grading service. PCGS coins often command premiums due to their strict standards.', specialties: ['Coins', 'Currency', 'Precious Metals'] },
];

const AUTH_SERVICES = [
  { icon: Eye, title: 'Visual Inspection', description: 'Expert examination of physical characteristics, printing quality, and material composition.' },
  { icon: Search, title: 'Database Verification', description: 'Cross-referencing serial numbers, population reports, and known counterfeit databases.' },
  { icon: Shield, title: 'Encapsulation', description: 'Professional grading companies seal items in tamper-evident holders with unique certification numbers.' },
  { icon: FileCheck, title: 'Certificate of Authenticity', description: 'Graded items receive a certificate and can be verified online through the grading company\'s database.' },
];

export default function GradingAuthPage() {
  return (
    <div className="min-h-screen bg-surface-base">
      <Header />

      {/* Hero */}
      <section className="py-20 bg-surface-card border-b border-border-subtle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 border border-gold/20 mb-6">
            <Award className="w-4 h-4 text-gold" />
            <span className="text-sm text-gold font-medium">Trust & Verification</span>
          </div>
          <h1 className="font-clash text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-6">
            Grading & <span className="gold-text">Authentication</span>
          </h1>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto mb-10">
            Understanding professional grading is essential for buying and selling with confidence. 
            Learn about the major grading companies, what their grades mean, and how authentication protects your investment.
          </p>
        </div>
      </section>

      {/* Why Grading Matters */}
      <section className="py-20 bg-surface-base">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-gold font-semibold mb-3">Why It Matters</p>
              <h2 className="font-clash text-3xl sm:text-4xl font-medium text-white mb-6">
                Grading Adds Value & Trust
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-6">
                Professional grading provides an objective, standardized assessment of an item's condition. 
                A graded item is authenticated, encapsulated, and assigned a numerical grade — giving buyers 
                confidence and sellers premium prices.
              </p>
              <div className="space-y-3">
                {[
                  'Authenticates the item against counterfeits',
                  'Provides a standardized condition assessment',
                  'Protects the item in a tamper-evident holder',
                  'Increases resale value — often significantly',
                  'Creates a permanent, verifiable record',
                ].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <p className="text-zinc-300 text-sm">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-8 bg-surface-card border border-border-subtle">
              <h3 className="font-clash text-xl font-semibold text-white mb-6">The Authentication Process</h3>
              <div className="space-y-6">
                {AUTH_SERVICES.map(s => (
                  <div key={s.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gold/10 flex items-center justify-center flex-shrink-0">
                      <s.icon className="w-5 h-5 text-gold" />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-white mb-1">{s.title}</h4>
                      <p className="text-xs text-zinc-400">{s.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Grading Companies */}
      <section className="py-20 bg-surface-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] uppercase text-gold font-semibold mb-3">Industry Leaders</p>
            <h2 className="font-clash text-3xl sm:text-4xl font-medium text-white">Major Grading Companies</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {GRADING_COMPANIES.map(gc => (
              <div key={gc.name} className="p-6 bg-surface-base border border-border-subtle hover:border-gold/30 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gold/10 flex items-center justify-center">
                    <span className="font-clash text-sm font-bold text-gold">{gc.name.split(' ')[0]}</span>
                  </div>
                  <div>
                    <h3 className="font-clash text-lg font-medium text-white">{gc.name}</h3>
                    <p className="text-xs text-zinc-500">{gc.full}</p>
                  </div>
                </div>
                <p className="text-sm text-zinc-400 leading-relaxed mb-4">{gc.description}</p>
                <div className="flex flex-wrap gap-2">
                  {gc.specialties.map(s => (
                    <span key={s} className="px-2 py-1 text-xs bg-surface-elevated text-zinc-400 border border-border-subtle">{s}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-surface-base">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-clash text-3xl font-medium text-white mb-4">Ready to Buy or Sell Graded Items?</h2>
          <p className="text-zinc-400 mb-8">
            Browse our marketplace for professionally graded coins, cards, and collectibles — or list your own graded items with confidence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/marketplace?condition=graded" className="btn-gold px-8 py-4 inline-flex items-center justify-center gap-2 uppercase tracking-wider text-sm">
              Browse Graded Items <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/create-listing" className="btn-secondary px-8 py-4 inline-flex items-center justify-center gap-2 uppercase tracking-wider text-sm">
              List Your Graded Items
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
